import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { ArrowLeft, Plus } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { SearchableSelect } from '@/components/common/searchable-select'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Spinner } from '@/components/ui/spinner'
import { jobsApi } from '@/services/jobs'
import crewsApi from '@/services/crews'
import { categoriesApi } from '@/services/categories'
import { locationsApi } from '@/services/locations'
import { workersApi } from '@/services/workers'
import { assignmentsApi } from '@/services/assignments'
import { orgUsersApi, type OrgUser } from '@/services/org-users'
import type { StoreOrderSummary } from '@/types'
import { getErrorMessage } from '@/lib/api'
import { normalizeOrgUserGroupKey } from '@/lib/org-user-groups'
import { getMapSearchCity } from '@/lib/map-location'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { useWorkspaceLabels } from '@/contexts/workspace-portal-context'
import { getUserDropdownKeywords, getUserDropdownLabel } from '@/lib/user-display'
import { scheduledHoursFromSchedule } from '@/lib/job-schedule-form'
import { cn, slugify } from '@/lib/utils'
import { OrderSelector } from '@/components/orders/order-selector'
import { JobScheduleFields, bindJobScheduleFormFields } from '@/components/jobs/job-schedule-fields'
import { JobAcceptanceModeFields, bindJobAcceptanceModeFormFields } from '@/components/jobs/job-acceptance-mode-fields'
import {
  ADVANCE_EXCEEDS_BUDGET_MESSAGE,
  JobCreateForm,
  bindJobCreateForm,
  focusFirstJobCreateFormError,
  type JobCreateAssignmentKind,
} from '@/components/jobs/job-create-form'
import {
  JobMediaGalleryEditor,
  releasePendingJobMediaFiles,
  uploadPendingJobMedia,
  type PendingJobMediaFile,
} from '@/components/jobs/job-media-gallery-editor'
import { EventCoverMediaFields } from '@/components/events/event-cover-media-fields'
import { useAuthStore } from '@/stores/auth-store'
import eventTagsApi from '@/services/event-tags'
import { EventTagsSelect } from '@/components/events/event-tags-select'
import { JobCreateFromStatementCard } from '@/components/jobs/job-create-from-statement-card'
import type { AiAssistLocationState, AiAssistParseResult } from '@/services/ai-assist'

const jobSchema = z.object({
  title: z.string().optional(),
  short_description: z.string().max(280, 'Short description must be 280 characters or less').optional(),
  description: z.string().optional(),
  job_code: z.string().optional(),
  job_landmark: z.string().optional(),
  order_reference_id: z.string().optional(),
  recurrence_rule: z.enum(['none', 'daily', 'weekly', 'bi_weekly', 'monthly', 'every_n_days', 'weekly_days', 'monthly_day']).default('none'),
  recurrence_interval_days: z.string().optional(),
  recurrence_weekdays: z.array(z.number().int().min(0).max(6)).default([]),
  recurrence_month_day: z.string().optional(),
  recurrence_count: z.string().optional(),
  recurrence_end: z.string().optional(),
  category: z.string().trim().min(1, 'Category is required'),
  tag_ids: z.array(z.string()).default([]),
  primary_worker: z.string().optional(),
  team_template: z.string().optional(),
  staffed_team: z.string().optional(),
  client_user: z.string().optional(),
  client_name: z.string().trim().optional(),
  client_contact: z.string().optional(),
  email: z.string().trim().email('Enter a valid email').optional().or(z.literal('')),
  phone: z.string().trim().optional(),
  location_id: z.string().optional(),
  location: z.string().optional(),
  city: z.string().optional(),
  address: z.string().optional(),
  status: z.string().default('draft'),
  priority: z.string().default('medium'),
  start_date: z.string().optional(),
  start_time: z.string().optional(),
  end_date: z.string().optional(),
  end_time: z.string().optional(),
  processing_time: z.string().optional(),
  processing_unit: z.enum(['min', 'hr', 'day']).default('hr'),
  estimated_hours: z.coerce.number().optional(),
  budget: z.coerce.number().optional(),
  advance_payment: z.coerce.number().optional(),
  assignment_acceptance_mode: z.enum(['manual', 'auto']).default('manual'),
  workday_entry_acceptance_mode: z.enum(['manual', 'auto']).default('manual'),
  workday_entry_approval_mode: z.enum(['manual', 'auto']).default('manual'),
}).superRefine((data, ctx) => {
  if (data.recurrence_rule === 'every_n_days') {
    const interval = Number(data.recurrence_interval_days)
    if (!Number.isFinite(interval) || interval <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Enter the number of days between recurring jobs.',
        path: ['recurrence_interval_days'],
      })
    }
  }
  if (data.recurrence_rule === 'weekly_days' && data.recurrence_weekdays.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Select at least one weekday.',
      path: ['recurrence_weekdays'],
    })
  }
  if (data.recurrence_rule === 'monthly_day') {
    const day = Number(data.recurrence_month_day)
    if (!Number.isInteger(day) || day < 1 || day > 31) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Enter a day of month between 1 and 31.',
        path: ['recurrence_month_day'],
      })
    }
  }
  if (data.recurrence_rule !== 'none' && data.recurrence_count) {
    const count = Number(data.recurrence_count)
    if (!Number.isInteger(count) || count <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Occurrences must be a positive number.',
        path: ['recurrence_count'],
      })
    }
  }
  // A blank budget is sent as job_pay 0, so any advance above that is rejected too.
  if (data.advance_payment && data.advance_payment > (data.budget ?? 0)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: ADVANCE_EXCEEDS_BUDGET_MESSAGE,
      path: ['advance_payment'],
    })
  }
})

const jobSchemaWithSchedule = jobSchema.superRefine((data, ctx) => {
  if (!String(data.start_date ?? '').trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Start date is required',
      path: ['start_date'],
    })
  }
  if (!String(data.start_time ?? '').trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Start time is required',
      path: ['start_time'],
    })
  }
})

type JobFormData = z.infer<typeof jobSchema>
type ClientGroupType = 'retail_customer' | 'business_customer'

const clientSegmentButtonClassName = (active: boolean) =>
  cn(
    'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
    active
      ? 'bg-sky-600 text-white shadow-sm'
      : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
  )

const getCustomerOptionValue = (client: OrgUser) => String(client.user_id ?? client.id)

const resolveCustomerGroupId = (
  groups: Array<{ id?: string | number; unique_code?: string | null; title?: string | null }> | undefined,
  clientGroupType: ClientGroupType,
) => {
  const targetKey = clientGroupType === 'business_customer' ? 'business_customer' : 'retail_customer'
  const normalizedGroups = groups ?? []

  const directMatch = normalizedGroups.find((group) => {
    const codeKey = normalizeOrgUserGroupKey(group.unique_code)
    const titleKey = normalizeOrgUserGroupKey(group.title)
    return codeKey === targetKey || titleKey === targetKey
  })
  if (directMatch?.id != null) {
    return directMatch.id
  }

  if (targetKey === 'retail_customer') {
    const legacyMatch = normalizedGroups.find((group) => {
      const codeKey = normalizeOrgUserGroupKey(group.unique_code)
      const titleKey = normalizeOrgUserGroupKey(group.title)
      return codeKey === 'client' || titleKey === 'client' || codeKey === 'customer' || titleKey === 'customer'
    })
    if (legacyMatch?.id != null) {
      return legacyMatch.id
    }
  }

  return null
}

const getCustomerDisplayName = (client: OrgUser) => {
  const name = [client.fname, client.lname]
    .map((part) => String(part ?? '').trim())
    .filter(Boolean)
    .join(' ')
    .trim()

  const baseName = name || String(client.email ?? '').trim() || `Client ${client.id}`
  const regNum = String(client.usr_reg_num ?? '').trim()
  if (!regNum || regNum === '0') {
    return baseName
  }

  return `${baseName} (${regNum})`
}

const splitIsoDateTime = (value?: string | null): { date: string; time: string } => {
  const raw = String(value || '').trim()
  if (!raw) {
    return { date: '', time: '' }
  }

  const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T')
  const [datePart, timePart = ''] = normalized.split('T')
  const time = timePart.slice(0, 5)
  return {
    date: /^\d{4}-\d{2}-\d{2}$/.test(datePart) ? datePart : '',
    time: /^\d{2}:\d{2}$/.test(time) ? time : '',
  }
}

const JOB_FORM_PRIORITIES = new Set(['low', 'medium', 'high', 'urgent', 'critical'])
const JOB_FORM_STATUSES = new Set(['draft', 'scheduled', 'in_progress', 'completed', 'cancelled', 'on_hold'])

export default function JobCreatePage() {
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const storedServiceInstanceId = useAuthStore((state) => state.serviceInstanceId)
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search) || storedServiceInstanceId
  const labels = useWorkspaceLabels()
  const isEventMode = labels.mode === 'event'
  const isProjectMode = labels.mode === 'project'
  const isJobMode = labels.mode === 'job'
  const requestedKind = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const kind = params.get('kind')?.trim().toLowerCase()
    return kind === 'committee' || kind === 'task' ? kind : null
  }, [location.search])
  const isCommitteeCreate = requestedKind === 'committee'
  const isTaskCreate = requestedKind === 'task'
  const isLightForm = isJobMode || isCommitteeCreate
  const entityLabel = labels.entity
  const createEntityLabel = isCommitteeCreate ? 'Committee' : isTaskCreate ? 'Task' : entityLabel
  const entityListPath = labels.basePath
  const isRecurringCreate = location.pathname.replace(/\/+$/, '').endsWith('/recurring/create')
  const listPath = isCommitteeCreate ? '/committees' : isRecurringCreate ? '/job/recurring' : entityListPath
  const parentEventId = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return params.get('parent_event_id')?.trim() || ''
  }, [location.search])
  const isSubEventCreate = isEventMode && Boolean(parentEventId)
  const categoryKind = isCommitteeCreate ? 'committee' : isEventMode ? 'event' : isProjectMode ? 'project' : 'job'
  const [selectedOrder, setSelectedOrder] = useState<StoreOrderSummary | null>(null)
  const [clientGroupType, setClientGroupType] = useState<ClientGroupType>('retail_customer')
  const [customerSearch, setCustomerSearch] = useState('')
  const [debouncedCustomerSearch, setDebouncedCustomerSearch] = useState('')
  const [resolvedSelectedClient, setResolvedSelectedClient] = useState<OrgUser | null>(null)
  const [assignmentKind, setAssignmentKind] = useState<JobCreateAssignmentKind>('worker')
  const [selectedWorkerIds, setSelectedWorkerIds] = useState<string[]>([])
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false)
  const [categoryName, setCategoryName] = useState('')
  const { data: categoriesResponse, isLoading: isCategoriesLoading } = useQuery({
    queryKey: ['job-categories', categoryKind],
    queryFn: () => categoriesApi.getCategories({
      kind: categoryKind,
    }),
  })
  const { data: jobManagementConfig } = useQuery({
    queryKey: ['job-management-configuration'],
    queryFn: jobsApi.getManagementConfiguration,
    enabled: Boolean(serviceInstanceId),
  })
  const { data: eventTags = [], isLoading: isEventTagsLoading } = useQuery({
    queryKey: ['event-tags', serviceInstanceId ?? 'none'],
    queryFn: () => eventTagsApi.list(),
    enabled: isEventMode && Boolean(serviceInstanceId),
  })
  const form = useForm<JobFormData>({
    resolver: zodResolver(isCommitteeCreate ? jobSchema : jobSchemaWithSchedule),
    // Job mode focuses the first invalid field in on-screen order itself.
    shouldFocusError: !isJobMode,
    defaultValues: {
      order_reference_id: '',
      short_description: '',
      recurrence_rule: isRecurringCreate ? 'weekly_days' : 'none',
      recurrence_interval_days: '',
      recurrence_weekdays: isRecurringCreate ? [1, 2, 3, 4, 5] : [],
      recurrence_month_day: '',
      recurrence_count: '',
      recurrence_end: '',
      category: '',
      tag_ids: [],
      primary_worker: 'unassigned',
      team_template: 'none',
      staffed_team: 'none',
      client_user: 'none',
      location_id: 'none',
      status: isCommitteeCreate ? 'active' : 'draft',
      priority: 'medium',
      start_time: '09:00',
      processing_unit: 'hr',
      assignment_acceptance_mode: 'manual',
      workday_entry_acceptance_mode: 'manual',
      workday_entry_approval_mode: 'manual',
    },
  })
  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = form
  const selectedCategory = watch('category')
  const selectedPrimaryWorker = watch('primary_worker')
  const selectedTeamTemplate = watch('team_template')
  const selectedStaffedTeam = watch('staffed_team')
  const selectedClientUser = watch('client_user')
  const selectedLocationId = watch('location_id')
  const recurrenceRule = watch('recurrence_rule')
  const isRecurrentBookingEnabled = jobManagementConfig?.enable_recurrent_job_booking !== false
  const { data: workersResponse, isLoading: isWorkersLoading } = useQuery({
    queryKey: ['job-primary-workers', selectedCategory],
    queryFn: () => workersApi.getWorkers({ page_size: 200, active: true, category: selectedCategory }),
    enabled: Boolean(selectedCategory),
  })
  const { data: teamTemplates = [], isLoading: isTeamTemplatesLoading } = useQuery({
    queryKey: ['job-team-templates', serviceInstanceId ?? 'none'],
    queryFn: () => crewsApi.listTemplates({
      service_instance_id: serviceInstanceId ?? undefined,
      active: true,
      page_size: 200,
    }),
    enabled: Boolean(serviceInstanceId),
    staleTime: 60_000,
  })
  const { data: assignableTeams = [], isLoading: isAssignableTeamsLoading } = useQuery({
    queryKey: ['job-assignable-teams', serviceInstanceId ?? 'none'],
    queryFn: () => crewsApi.listCrews({
      service_instance_id: serviceInstanceId ?? undefined,
      ready: true,
      active: true,
      page_size: 200,
    }),
    enabled: Boolean(isJobMode && assignmentKind === 'team' && serviceInstanceId),
    staleTime: 30_000,
  })
  const { data: staffedTeams = [], isLoading: isStaffedTeamsLoading } = useQuery({
    queryKey: ['job-staffed-teams', serviceInstanceId ?? 'none', selectedTeamTemplate ?? 'none'],
    queryFn: () => crewsApi.listCrews({
      service_instance_id: serviceInstanceId ?? undefined,
      crew_template_id: selectedTeamTemplate !== 'none' ? selectedTeamTemplate : undefined,
      ready: true,
      active: true,
      page_size: 200,
    }),
    enabled: Boolean(serviceInstanceId && selectedTeamTemplate && selectedTeamTemplate !== 'none'),
    staleTime: 30_000,
  })
  const { data: clientGroupsResponse, isLoading: isClientGroupsLoading } = useQuery({
    queryKey: ['job-client-groups', serviceInstanceId ?? 'none'],
    queryFn: () => orgUsersApi.listGroups({ serviceInstanceId }),
    enabled: Boolean(serviceInstanceId),
    staleTime: 60_000,
  })
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedCustomerSearch(customerSearch.trim())
    }, 300)

    return () => window.clearTimeout(timeoutId)
  }, [customerSearch])

  const selectedClientGroupId = useMemo(
    () => resolveCustomerGroupId(clientGroupsResponse, clientGroupType),
    [clientGroupType, clientGroupsResponse],
  )

  const canLoadCustomers = Boolean(serviceInstanceId && selectedClientGroupId)

  const {
    data: clientsPage,
    isLoading: isClientsLoading,
    isFetching: isClientsFetching,
  } = useQuery({
    queryKey: ['job-clients', serviceInstanceId ?? 'none', selectedClientGroupId ?? 'none', clientGroupType, debouncedCustomerSearch],
    queryFn: () => orgUsersApi.listPaginated({
      search: debouncedCustomerSearch,
      serviceInstanceId,
      userGroupId: selectedClientGroupId ?? undefined,
      page: 1,
      pageSize: 200,
      includeCustomerActivity: true,
    }),
    enabled: canLoadCustomers,
    staleTime: 20_000,
  })
  const { data: locationsResponse = [], isLoading: isLocationsLoading } = useQuery({
    queryKey: ['job-locations', serviceInstanceId ?? 'none'],
    queryFn: () => locationsApi.list({
      service_instance_id: serviceInstanceId ?? undefined,
    }),
    enabled: Boolean(serviceInstanceId),
    staleTime: 60_000,
  })
  const categories = categoriesResponse?.results ?? []
  const categoryOptions = useMemo(
    () => categories.map((category) => ({
      value: String(category.id),
      label: category.name,
      keywords: [category.name, category.slug, category.kind].filter(Boolean).join(' '),
    })),
    [categories],
  )
  const workers = workersResponse?.results ?? []
  const clients = clientsPage?.results ?? []
  const locations = locationsResponse
  const clientOptions = useMemo(() => {
    const options = clients.map((client) => ({
      value: getCustomerOptionValue(client),
      label: getCustomerDisplayName(client),
      keywords: [client.fname, client.lname, client.email, client.phone, client.username, client.usr_reg_num].filter(Boolean).join(' '),
    }))

    if (
      resolvedSelectedClient
      && !options.some((option) => option.value === getCustomerOptionValue(resolvedSelectedClient))
    ) {
      options.unshift({
        value: getCustomerOptionValue(resolvedSelectedClient),
        label: getCustomerDisplayName(resolvedSelectedClient),
        keywords: [
          resolvedSelectedClient.fname,
          resolvedSelectedClient.lname,
          resolvedSelectedClient.email,
          resolvedSelectedClient.phone,
          resolvedSelectedClient.username,
          resolvedSelectedClient.usr_reg_num,
        ].filter(Boolean).join(' '),
      })
    }

    return [
      { value: 'none', label: 'No linked customer', keywords: 'none unlinked' },
      ...options,
    ]
  }, [clients, resolvedSelectedClient])

  const assignableTeamOptions = useMemo(
    () => assignableTeams.map((team) => ({
      value: String(team.id),
      label: team.name,
      keywords: `${team.name ?? ''} ${team.code ?? ''}`.trim(),
    })),
    [assignableTeams],
  )
  const primaryWorkerOptions = useMemo(
    () => [
      { value: 'unassigned', label: 'No primary worker', keywords: 'unassigned none' },
      ...workers.map((worker) => ({
        value: String(worker.id),
        label: getUserDropdownLabel(worker, worker.id),
        keywords: getUserDropdownKeywords(worker),
      })),
    ],
    [workers],
  )
  const teamTemplateOptions = useMemo(
    () => [
      { value: 'none', label: 'No team template', keywords: 'none unassigned' },
      ...teamTemplates.map((template) => ({
        value: String(template.id),
        label: template.name,
        keywords: `${template.name ?? ''} ${template.code ?? ''}`.trim(),
      })),
    ],
    [teamTemplates],
  )
  const staffedTeamOptions = useMemo(
    () => [
      { value: 'none', label: 'No staffed team', keywords: 'none unassigned' },
      ...staffedTeams.map((team) => ({
        value: String(team.id),
        label: team.name,
        keywords: `${team.name ?? ''} ${team.code ?? ''}`.trim(),
      })),
    ],
    [staffedTeams],
  )

  useEffect(() => {
    if (!selectedCategory && selectedPrimaryWorker !== 'unassigned') {
      setValue('primary_worker', 'unassigned')
      return
    }

    if (
      !isWorkersLoading &&
      selectedPrimaryWorker &&
      selectedPrimaryWorker !== 'unassigned' &&
      !workers.some((worker) => String(worker.id) === selectedPrimaryWorker)
    ) {
      setValue('primary_worker', 'unassigned')
    }
  }, [isWorkersLoading, selectedCategory, selectedPrimaryWorker, setValue, workers])

  useEffect(() => {
    // Workers are listed per category, so a new category starts a new selection.
    setSelectedWorkerIds([])
  }, [selectedCategory])

  useEffect(() => {
    if (!selectedTeamTemplate || selectedTeamTemplate === 'none') {
      if (selectedStaffedTeam && selectedStaffedTeam !== 'none') {
        setValue('staffed_team', 'none')
      }
      return
    }

    if (
      !isStaffedTeamsLoading
      && selectedStaffedTeam
      && selectedStaffedTeam !== 'none'
      && !staffedTeams.some((team) => String(team.id) === selectedStaffedTeam)
    ) {
      setValue('staffed_team', 'none')
    }
  }, [isStaffedTeamsLoading, selectedStaffedTeam, selectedTeamTemplate, setValue, staffedTeams])

  useEffect(() => {
    if (!selectedClientUser || selectedClientUser === 'none') {
      setResolvedSelectedClient(null)
      return
    }

    const matchedClient = clients.find((item) => getCustomerOptionValue(item) === selectedClientUser)
    if (matchedClient) {
      setResolvedSelectedClient(matchedClient)
      return
    }

    let cancelled = false

    void orgUsersApi.getClient(selectedClientUser).then((client) => {
      if (cancelled) {
        return
      }

      setResolvedSelectedClient(client)

      const groupKey = normalizeOrgUserGroupKey(client.user_group_code || client.user_group_title)
      if (groupKey === 'business_customer') {
        setClientGroupType('business_customer')
      } else if (groupKey === 'retail_customer') {
        setClientGroupType('retail_customer')
      }
    }).catch(() => {
      // Ignore lookup failures; manual client fields can still be used.
    })

    return () => {
      cancelled = true
    }
  }, [clients, selectedClientUser])

  useEffect(() => {
    if (!selectedLocationId || selectedLocationId === 'none') {
      return
    }

    const selectedLocation = locations.find((item) => String(item.id) === selectedLocationId)
    if (!selectedLocation) {
      if (!isLocationsLoading) {
        setValue('location_id', 'none', { shouldDirty: true })
      }
      return
    }

    setValue('location', selectedLocation.place, { shouldDirty: true })
    setValue('city', getMapSearchCity({ label: selectedLocation.place }), { shouldDirty: true })

    if (selectedLocation.client_user && String(selectedLocation.client_user) !== selectedClientUser) {
      setValue('client_user', String(selectedLocation.client_user), { shouldDirty: true })
    }
  }, [isLocationsLoading, locations, selectedClientUser, selectedLocationId, setValue])

  useEffect(() => {
    if (!selectedClientUser || selectedClientUser === 'none') {
      return
    }

    const client = clients.find((item) => getCustomerOptionValue(item) === selectedClientUser)
    if (!client) {
      return
    }

    const fullName = [client.fname, client.lname].filter(Boolean).join(' ').trim()
    const email = client.email || client.email_data?.find((entry) => entry.email)?.email || ''
    const phone = client.phone || client.phonenumber_data?.find((entry) => entry.mobile_number)?.mobile_number || ''

    setValue('client_name', fullName || '', { shouldDirty: true })
    setValue('email', email, { shouldDirty: true, shouldValidate: true })
    setValue('phone', phone, { shouldDirty: true })
  }, [clients, selectedClientUser, setValue])

  const handleClientGroupTypeChange = (next: ClientGroupType) => {
    setClientGroupType(next)
    setCustomerSearch('')
    setDebouncedCustomerSearch('')
    if (selectedClientUser && selectedClientUser !== 'none') {
      setValue('client_user', 'none', { shouldDirty: true })
    }
  }

  const [pendingMedia, setPendingMedia] = useState<PendingJobMediaFile[]>([])
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null)
  const [bannerFile, setBannerFile] = useState<File | null>(null)

  useEffect(() => () => releasePendingJobMediaFiles(pendingMedia), [pendingMedia])

  const registeredClientLabel = clientGroupType === 'business_customer' ? 'Registered business customer' : 'Registered retail customer'
  const clientDirectoryUnavailable = !serviceInstanceId
  const clientDirectoryPending = isClientGroupsLoading || (canLoadCustomers && (isClientsLoading || isClientsFetching))

  const createMutation = useMutation({
    mutationFn: (payload: Parameters<typeof jobsApi.create>[0]) => (
      isCommitteeCreate
        ? jobsApi.createCommittee(payload)
        : isTaskCreate
          ? jobsApi.createTask(payload)
          : isEventMode
            ? jobsApi.createEvent(payload)
            : isProjectMode
              ? jobsApi.createProject(payload)
              : jobsApi.create(payload)
    ),
    onSuccess: async (data) => {
      const hadPendingMedia = pendingMedia.length > 0
      const hasEventCoverUploads = Boolean(isEventMode && (thumbnailFile || bannerFile))
      try {
        if (hadPendingMedia) {
          await uploadPendingJobMedia(data.id, pendingMedia)
          releasePendingJobMediaFiles(pendingMedia)
          setPendingMedia([])
        }
        if (isEventMode && thumbnailFile) {
          await jobsApi.uploadImageField(data.id, 'thumbnail', thumbnailFile, {
            service_instance_id: serviceInstanceId ?? undefined,
          })
          setThumbnailFile(null)
        }
        if (isEventMode && bannerFile) {
          await jobsApi.uploadImageField(data.id, 'banner', bannerFile, {
            service_instance_id: serviceInstanceId ?? undefined,
          })
          setBannerFile(null)
        }
        toast.success(
          hadPendingMedia || hasEventCoverUploads
            ? `${createEntityLabel} and media created successfully`
            : `${createEntityLabel} created successfully`,
        )
      } catch (error) {
        toast.error(getErrorMessage(error))
        toast.message(`${entityLabel} was created, but some media uploads failed. Open the record to retry.`)
      }
      if (isJobMode && assignmentKind === 'worker' && selectedWorkerIds.length > 0) {
        // Job/Add has no multi-worker field; selected workers are added as
        // equal JobAssignment rows through the assignment API.
        let failedAssignments = 0
        for (const workerId of selectedWorkerIds) {
          try {
            await assignmentsApi.createAssignment({ job: data.id, worker: workerId })
          } catch (error) {
            failedAssignments += 1
            toast.error(getErrorMessage(error))
          }
        }
        if (failedAssignments > 0) {
          toast.message(`${entityLabel} was created, but some workers could not be assigned. Open the ${entityLabel.toLowerCase()} to retry.`)
        }
      }
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
      if (isSubEventCreate && parentEventId) {
        queryClient.invalidateQueries({ queryKey: ['sub-events', parentEventId] })
      }
      navigate(withServiceInstanceId(
        isCommitteeCreate ? `/committees/${data.id}` : `${entityListPath}/${data.id}`,
        serviceInstanceId,
      ))
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const createCategoryMutation = useMutation({
    mutationFn: (name: string) => {
      const trimmed = name.trim()
      return categoriesApi.createCategory(
        {
          name: trimmed,
          slug: slugify(trimmed) || `category-${Date.now()}`,
          kind: 'committee',
        },
        {
          kind: 'committee',
          scope: 'local',
          service_instance_id: serviceInstanceId || undefined,
          serviceInstanceHeader: serviceInstanceId || undefined,
        },
      )
    },
    onSuccess: (category) => {
      toast.success('Category added')
      queryClient.invalidateQueries({ queryKey: ['job-categories', 'committee'] })
      setValue('category', String(category.id), { shouldDirty: true, shouldValidate: true })
      setCategoryName('')
      setCategoryDialogOpen(false)
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Unable to create the category.'))
    },
  })

  const submitNewCategory = () => {
    const trimmed = categoryName.trim()
    if (!trimmed || createCategoryMutation.isPending) return
    createCategoryMutation.mutate(trimmed)
  }

  const onSubmit = (data: JobFormData) => {
    if (isCommitteeCreate && !data.title?.trim()) {
      toast.error('Title is required.')
      return
    }
    const estimatedHours = scheduledHoursFromSchedule(
      data.start_date,
      data.start_time,
      data.end_date,
      data.end_time,
      data.processing_time,
      data.processing_unit,
    )
    const categoryLabel = categories.find((category) => String(category.id) === data.category)?.name || data.category
    const fallbackTitle = String(categoryLabel || '')
      .replace(/^\[SEED:[^\]]+\]\s*/, '')
      .trim()

    createMutation.mutate({
      ...data,
      title: data.title?.trim() || fallbackTitle || (isCommitteeCreate ? 'New committee' : 'New job'),
      client: isCommitteeCreate ? 'community' : data.client_name,
      visibility: isCommitteeCreate ? 'public' : undefined,
      status: isCommitteeCreate
        ? (data.status === 'dissolved' || data.status === 'suspended' ? data.status : 'active')
        : isLightForm ? 'draft' : data.status,
      start_date: isCommitteeCreate ? '' : data.start_date,
      start_time: isCommitteeCreate ? '' : data.start_time,
      end_date: isCommitteeCreate ? '' : data.end_date,
      end_time: isCommitteeCreate ? '' : data.end_time,
      short_description: isEventMode ? (data.short_description?.trim() || null) : undefined,
      job_code: data.job_code?.trim() || null,
      job_landmark: data.job_landmark?.trim() || null,
      parent_event_id: isSubEventCreate ? parentEventId : null,
      estimated_hours: isCommitteeCreate ? undefined : estimatedHours,
      recurrence_interval_days: data.recurrence_rule === 'every_n_days' && data.recurrence_interval_days
        ? Number(data.recurrence_interval_days)
        : null,
      recurrence_weekdays: data.recurrence_rule === 'weekly_days' ? data.recurrence_weekdays : [],
      recurrence_month_day: data.recurrence_rule === 'monthly_day' && data.recurrence_month_day
        ? Number(data.recurrence_month_day)
        : null,
      recurrence_count: data.recurrence_rule !== 'none' && data.recurrence_count
        ? Number(data.recurrence_count)
        : null,
      recurrence_end: data.recurrence_rule !== 'none' && data.recurrence_end ? data.recurrence_end : null,
      order_reference_id: data.order_reference_id?.trim() || null,
      primary_worker: (!isLightForm || assignmentKind === 'worker') && data.primary_worker && data.primary_worker !== 'unassigned'
        ? data.primary_worker
        : undefined,
      team_template: isLightForm ? null : (data.team_template && data.team_template !== 'none' ? data.team_template : null),
      staffed_team: (isLightForm ? assignmentKind === 'team' : true) && data.staffed_team && data.staffed_team !== 'none'
        ? data.staffed_team
        : null,
      client_user: data.client_user && data.client_user !== 'none' ? data.client_user : null,
      location_id: data.location_id && data.location_id !== 'none' ? data.location_id : null,
      tag_ids: isEventMode ? data.tag_ids.map((tagId) => Number(tagId)).filter((tagId) => Number.isFinite(tagId)) : undefined,
    })
  }

  const status = watch('status')
  const priority = watch('priority')

  const resolveAiCategoryId = (draft: AiAssistParseResult['draft']) => {
    const rawCategory = String(draft?.category || '').trim()
    if (rawCategory) {
      const normalizedCategory = rawCategory.toLowerCase()
      const exactMatch = categories.find((category) => {
        const categoryId = String(category.id)
        const categoryName = String(category.name || '').trim().toLowerCase()
        const categorySlug = String(category.slug || '').trim().toLowerCase()
        return (
          categoryId === rawCategory
          || categoryName === normalizedCategory
          || categorySlug === normalizedCategory
        )
      })

      if (exactMatch) {
        return String(exactMatch.id)
      }

      const keywordMatch = categories.find((category) => {
        const haystack = [category.name, category.slug, draft?.title, draft?.description, draft?.client, draft?.customer_name, draft?.job_landmark]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
        return haystack.includes(normalizedCategory)
      })

      if (keywordMatch) {
        return String(keywordMatch.id)
      }
    }

    if (categories.length > 0) {
      return String(categories[0].id)
    }

    return ''
  }

  const applyAiDraft = (parsed: AiAssistParseResult) => {
    const draft = parsed.draft || {}
    const options = { shouldDirty: true, shouldValidate: true } as const

    const aiTitle = String(draft.title || '').trim()
    if (aiTitle) {
      setValue('title', aiTitle, options)
    } else if (draft.description || draft.client || draft.customer_name) {
      const fallbackTitle = [draft.client, draft.customer_name, draft.description]
        .map((value) => String(value || '').trim())
        .find(Boolean)

      if (fallbackTitle) {
        setValue('title', fallbackTitle.slice(0, 80), options)
      }
    }
    if (draft.description != null) {
      setValue('description', String(draft.description), options)
    }
    const resolvedCategoryId = resolveAiCategoryId(draft)
    if (resolvedCategoryId) {
      setValue('category', resolvedCategoryId, options)
    }

    const clientName = String(draft.customer_name || draft.client || '').trim()
    if (clientName) {
      setValue('client_name', clientName, options)
    }
    if (draft.email != null) {
      setValue('email', String(draft.email), options)
    }
    if (draft.phone != null) {
      setValue('phone', String(draft.phone), options)
    }
    if (draft.first_line != null) {
      setValue('address', String(draft.first_line), options)
    }
    if (draft.city != null) {
      setValue('city', String(draft.city), options)
    }
    if (draft.job_landmark != null) {
      setValue('job_landmark', String(draft.job_landmark), options)
    }

    const priorityValue = String(draft.priority || '').trim().toLowerCase()
    if (JOB_FORM_PRIORITIES.has(priorityValue)) {
      setValue('priority', priorityValue, options)
    }

    const statusValue = String(draft.status || '').trim().toLowerCase()
    if (JOB_FORM_STATUSES.has(statusValue)) {
      setValue('status', statusValue, options)
    }

    const start = splitIsoDateTime(
      typeof draft.scheduled_start === 'string' ? draft.scheduled_start : null,
    )
    if (start.date) {
      setValue('start_date', start.date, options)
    }
    if (start.time) {
      setValue('start_time', start.time, options)
    }

    const end = splitIsoDateTime(
      typeof draft.scheduled_end === 'string' ? draft.scheduled_end : null,
    )
    if (end.date) {
      setValue('end_date', end.date, options)
    }
    if (end.time) {
      setValue('end_time', end.time, options)
    }

    if (draft.scheduled_hours != null && draft.scheduled_hours !== '') {
      const hours = Number(draft.scheduled_hours)
      if (Number.isFinite(hours)) {
        setValue('estimated_hours', hours, options)
      }
    }

    if (draft.job_pay != null && draft.job_pay !== '') {
      const budget = Number(draft.job_pay)
      if (Number.isFinite(budget)) {
        setValue('budget', budget, options)
      }
    }
  }

  useEffect(() => {
    if (!isJobMode) {
      return
    }
    const state = (location.state || null) as AiAssistLocationState | null
    const incoming = state?.aiAssistDraft
    if (!incoming || incoming.intent !== 'job') {
      return
    }
    applyAiDraft(incoming)
    toast.success('AI draft applied — review before saving.')
    navigate(`${location.pathname}${location.search}`, { replace: true, state: {} })
    // Apply once when arriving from the jobs AI sheet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(withServiceInstanceId(listPath, serviceInstanceId))}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {isSubEventCreate && !isCommitteeCreate && !isTaskCreate
              ? 'Add Sub Event'
              : isJobMode && isRecurringCreate
                ? 'Add Recurrence Job'
                : `Create ${createEntityLabel}`}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isCommitteeCreate
              ? 'A committee is a light job. Title, category, and status. Kind is committee.'
              : isSubEventCreate && !isTaskCreate
              ? `Create a child event under ${entityLabel.toLowerCase()} #${parentEventId}.`
              : isJobMode
                ? isRecurringCreate
                  ? 'Set up a job that repeats on a schedule. Fields marked * are required.'
                  : 'Fields marked * are required. New jobs are saved as Draft.'
                : `Add a new ${createEntityLabel.toLowerCase()} to the system`}
          </p>
        </div>
      </div>

      {!isLightForm ? (
        <JobCreateFromStatementCard
          serviceInstanceId={serviceInstanceId}
          disabled={createMutation.isPending}
          onApplyDraft={applyAiDraft}
        />
      ) : null}

      {isJobMode ? (
        <JobCreateForm
          form={bindJobCreateForm(form)}
          onSubmit={handleSubmit(onSubmit, focusFirstJobCreateFormError)}
          onCancel={() => navigate(withServiceInstanceId(listPath, serviceInstanceId))}
          showRecurrence={isRecurringCreate}
          serviceInstanceId={serviceInstanceId}
          submitLabel={`Create ${entityLabel.toLowerCase()}`}
          isSubmitting={createMutation.isPending}
          submitDisabled={createMutation.isPending || isCategoriesLoading || categories.length === 0}
          categoryOptions={categoryOptions}
          isCategoriesLoading={isCategoriesLoading}
          selectedOrder={selectedOrder}
          onOrderSelect={(order) => {
            setSelectedOrder(order)
            setValue('order_reference_id', order?.reference_id ?? '', { shouldDirty: true, shouldValidate: true })
          }}
          workers={workers}
          isWorkersLoading={isWorkersLoading}
          selectedWorkerIds={selectedWorkerIds}
          onSelectedWorkerIdsChange={setSelectedWorkerIds}
          assignmentKind={assignmentKind}
          onAssignmentKindChange={(value) => {
            setAssignmentKind(value)
            if (value === 'worker') {
              setValue('staffed_team', 'none')
              setValue('team_template', 'none')
            } else {
              setValue('primary_worker', 'unassigned')
              setSelectedWorkerIds([])
            }
          }}
          teamOptions={assignableTeamOptions}
          isTeamsLoading={isAssignableTeamsLoading}
        />
      ) : (
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className={cn('grid gap-6', isLightForm ? 'max-w-xl' : 'md:grid-cols-2')}>
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Basic Information</CardTitle>
              <CardDescription>{isCommitteeCreate ? 'Enter the committee details' : `Enter the ${entityLabel.toLowerCase()} details`}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!isJobMode || isCommitteeCreate ? <div className="space-y-2">
                <Label htmlFor="title">{`${createEntityLabel} Title`}</Label>
                <Input
                  id="title"
                  placeholder={isCommitteeCreate ? 'Committee title' : `Optional ${entityLabel.toLowerCase()} title`}
                  {...register('title')}
                  aria-invalid={!!errors.title}
                />
                {errors.title && (
                  <p className="text-sm text-destructive">{errors.title.message}</p>
                )}
              </div> : null}

              {!isLightForm ? <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="job_code">Code</Label>
                  <Input
                    id="job_code"
                    placeholder="Optional business code"
                    {...register('job_code')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="job_landmark">Landmark</Label>
                  <Input
                    id="job_landmark"
                    placeholder="Optional landmark or venue"
                    {...register('job_landmark')}
                  />
                </div>
              </div> : null}

              <div className={cn('gap-4', isEventMode ? 'grid sm:grid-cols-2' : 'space-y-2')}>
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <Label htmlFor="category">{isCommitteeCreate ? 'Committee category *' : isEventMode ? 'Event Category *' : 'Category *'}</Label>
                    {isCommitteeCreate ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2"
                        onClick={() => setCategoryDialogOpen(true)}
                      >
                        <Plus className="mr-1 h-3.5 w-3.5" />
                        Add category
                      </Button>
                    ) : null}
                  </div>
                  <Controller
                    control={control}
                    name="category"
                    render={({ field }) => (
                      <SearchableSelect
                        id="category"
                        ariaLabel={isEventMode ? 'Event category' : 'Category'}
                        value={field.value || ''}
                        onValueChange={field.onChange}
                        options={categoryOptions}
                        placeholder={isCategoriesLoading ? 'Loading categories...' : 'Search or select category'}
                        searchPlaceholder="Type a category name"
                        emptyText="No matching categories found."
                        emptySearchText="Start typing a category name."
                        disabled={isCategoriesLoading || categories.length === 0}
                      />
                    )}
                  />
                  {errors.category && (
                    <p className="text-sm text-destructive">{errors.category.message}</p>
                  )}
                  {!isCategoriesLoading && categories.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      {isCommitteeCreate
                        ? 'Add a committee category to continue.'
                        : isProjectMode
                        ? 'Create a category first before creating a project.'
                        : isEventMode
                          ? 'Create a category first before creating an event.'
                          : 'Create a category first before creating a job.'}
                    </p>
                  )}
                </div>

                {isEventMode && !isCommitteeCreate ? (
                  <div className="space-y-2">
                    <Label htmlFor="event-tags">Tags</Label>
                    <Controller
                      control={control}
                      name="tag_ids"
                      render={({ field }) => (
                        <EventTagsSelect
                          id="event-tags"
                          value={field.value ?? []}
                          onValueChange={field.onChange}
                          tags={eventTags}
                          disabled={isEventTagsLoading || !serviceInstanceId}
                          placeholder={isEventTagsLoading ? 'Loading tags...' : 'Select tags'}
                        />
                      )}
                    />
                  </div>
                ) : null}
              </div>

              {!isLightForm || isCommitteeCreate ? <div className="space-y-2">
                {isEventMode && !isCommitteeCreate ? (
                  <>
                    <Label htmlFor="short_description">Short Description</Label>
                    <Input
                      id="short_description"
                      maxLength={280}
                      placeholder="Short event summary for list/table"
                      {...register('short_description')}
                    />
                    {errors.short_description ? (
                      <p className="text-sm text-destructive">{errors.short_description.message}</p>
                    ) : null}
                  </>
                ) : null}
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder={isCommitteeCreate ? 'Committee description' : 'Enter job description'}
                  rows={4}
                  {...register('description')}
                />
              </div> : null}

              {isCommitteeCreate ? (
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Controller
                    control={control}
                    name="status"
                    render={({ field }) => (
                      <Select value={field.value || 'active'} onValueChange={field.onChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="suspended">Suspended</SelectItem>
                          <SelectItem value="dissolved">Dissolved</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
              ) : null}

              {!isLightForm ? (
                <OrderSelector
                  value={watch('order_reference_id')}
                  selectedOrder={selectedOrder}
                  onSelect={(order) => {
                    setSelectedOrder(order)
                    setValue('order_reference_id', order?.reference_id ?? '', { shouldDirty: true, shouldValidate: true })
                  }}
                  error={errors.order_reference_id?.message}
                />
              ) : null}

              {!isLightForm ? <>
              <div className="space-y-2">
                <Label htmlFor="primary_worker">Incharge</Label>
                <Controller
                  control={control}
                  name="primary_worker"
                  render={({ field }) => (
                    <SearchableSelect
                      id="primary_worker"
                      ariaLabel="Primary worker"
                      value={field.value || 'unassigned'}
                      onValueChange={field.onChange}
                      options={primaryWorkerOptions}
                      placeholder={!selectedCategory ? 'Select category first' : isWorkersLoading ? 'Loading workers...' : 'Select primary worker'}
                      searchPlaceholder="Search workers"
                      emptyText="No workers found."
                      disabled={!selectedCategory || isWorkersLoading}
                    />
                  )}
                />
                {selectedCategory && !isWorkersLoading && workers.length === 0 && (
                  <p className="text-sm text-muted-foreground">No workers are mapped to the selected category.</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="team_template">Team Template</Label>
                <Controller
                  control={control}
                  name="team_template"
                  render={({ field }) => (
                    <SearchableSelect
                      id="team_template"
                      ariaLabel="Team template"
                      value={field.value || 'none'}
                      onValueChange={field.onChange}
                      options={teamTemplateOptions}
                      placeholder={isTeamTemplatesLoading ? 'Loading team templates...' : 'Select team template'}
                      searchPlaceholder="Search team templates"
                      emptyText="No team templates found."
                      disabled={!serviceInstanceId || isTeamTemplatesLoading}
                    />
                  )}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="staffed_team">Staffed Team</Label>
                <Controller
                  control={control}
                  name="staffed_team"
                  render={({ field }) => (
                    <SearchableSelect
                      id="staffed_team"
                      ariaLabel="Staffed team"
                      value={field.value || 'none'}
                      onValueChange={field.onChange}
                      options={staffedTeamOptions}
                      placeholder={selectedTeamTemplate === 'none' ? 'Select team template first' : isStaffedTeamsLoading ? 'Loading staffed teams...' : 'Select staffed team'}
                      searchPlaceholder="Search staffed teams"
                      emptyText="No staffed teams found."
                      disabled={!serviceInstanceId || selectedTeamTemplate === 'none' || isStaffedTeamsLoading}
                    />
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={status}
                    onValueChange={(value) => setValue('status', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="on_hold">On Hold</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select
                    value={priority}
                    onValueChange={(value) => setValue('priority', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select priority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              </> : null}
            </CardContent>
          </Card>

          {/* Customer Info */}
          {!isLightForm ? <Card>
            <CardContent className="space-y-4 pt-6">
              <div className="space-y-2">
                <Label>Customer type</Label>
                <div className="inline-flex rounded-lg border border-border/70 bg-muted/20 p-0.5">
                  <button
                    type="button"
                    aria-pressed={clientGroupType === 'retail_customer'}
                    onClick={() => handleClientGroupTypeChange('retail_customer')}
                    className={clientSegmentButtonClassName(clientGroupType === 'retail_customer')}
                  >
                    Retail
                  </button>
                  <button
                    type="button"
                    aria-pressed={clientGroupType === 'business_customer'}
                    onClick={() => handleClientGroupTypeChange('business_customer')}
                    className={clientSegmentButtonClassName(clientGroupType === 'business_customer')}
                  >
                    Business
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="client_user">{registeredClientLabel}</Label>
                <Controller
                  control={control}
                  name="client_user"
                  render={({ field }) => (
                    <SearchableSelect
                      id="client_user"
                      ariaLabel={registeredClientLabel}
                      value={field.value || 'none'}
                      onValueChange={field.onChange}
                      onSearchChange={setCustomerSearch}
                      options={clientOptions}
                      placeholder={
                        !serviceInstanceId
                          ? 'Select a branch to load customers'
                          : isClientGroupsLoading
                            ? 'Loading customer groups...'
                            : !selectedClientGroupId
                              ? 'No customer group configured for this branch'
                              : clientDirectoryPending
                                ? 'Loading customers...'
                                : 'Select customer (optional)'
                      }
                      searchPlaceholder={`Search ${clientGroupType === 'business_customer' ? 'business' : 'retail'} customers by name, phone, or email`}
                      emptyText={
                        isClientGroupsLoading || isClientsLoading || isClientsFetching
                          ? 'Loading customers...'
                          : 'No customers found.'
                      }
                      emptySearchText={
                        isClientsFetching
                          ? 'Searching customers...'
                          : 'Type to search customers.'
                      }
                      showAllOnEmptySearch
                      filterLocally={false}
                      disabled={clientDirectoryUnavailable}
                    />
                  )}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="client_name">Customer Name</Label>
                <Input
                  id="client_name"
                  placeholder="Enter customer name"
                  {...register('client_name')}
                  aria-invalid={!!errors.client_name}
                />
                {errors.client_name && (
                  <p className="text-sm text-destructive">{errors.client_name.message}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Customer Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="client@example.com"
                    {...register('email')}
                    aria-invalid={!!errors.email}
                  />
                  {errors.email ? <p className="text-sm text-destructive">{errors.email.message}</p> : null}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Customer Phone</Label>
                  <Input
                    id="phone"
                    placeholder="9876543210"
                    {...register('phone')}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="location_id">Saved Location</Label>
                <Controller
                  control={control}
                  name="location_id"
                  render={({ field }) => (
                    <Select value={field.value || 'none'} onValueChange={field.onChange} disabled={isLocationsLoading}>
                      <SelectTrigger id="location_id">
                        <SelectValue placeholder={isLocationsLoading ? 'Loading locations...' : 'Select saved location (optional)'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No linked location</SelectItem>
                        {!isLocationsLoading && locations.length === 0 ? (
                          <SelectItem value="__no_saved_locations" disabled>No saved locations found</SelectItem>
                        ) : null}
                        {locations.map((savedLocation) => (
                          <SelectItem key={savedLocation.id} value={String(savedLocation.id)}>
                            {savedLocation.place}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  placeholder={`${entityLabel} location`}
                  {...register('location')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Textarea
                  id="address"
                  placeholder="Full address"
                  rows={2}
                  {...register('address')}
                />
              </div>
            </CardContent>
          </Card> : null}

          {/* Schedule */}
          {!isCommitteeCreate ? <Card>
            <CardHeader>
              <CardTitle className="text-base">Schedule</CardTitle>
              <CardDescription>Set job timeline</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <JobScheduleFields {...bindJobScheduleFormFields({ control, register, setValue, errors })} requiredOnly={isLightForm} />

              {(isRecurringCreate || (!isLightForm && isRecurrentBookingEnabled)) && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="recurrence_rule">Recurrence</Label>
                    <Controller
                      control={control}
                      name="recurrence_rule"
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger id="recurrence_rule">
                            <SelectValue placeholder="Select recurrence" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Does not recur</SelectItem>
                            <SelectItem value="daily">Daily</SelectItem>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="bi_weekly">Bi-weekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                            <SelectItem value="every_n_days">After specific number of days</SelectItem>
                            <SelectItem value="weekly_days">Weekly on selected days</SelectItem>
                            <SelectItem value="monthly_day">Monthly on a specific day</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>

                  {recurrenceRule === 'every_n_days' && (
                    <div className="space-y-2">
                      <Label htmlFor="recurrence_interval_days">Repeat every N days</Label>
                      <Input
                        id="recurrence_interval_days"
                        type="number"
                        min="1"
                        placeholder="Enter days"
                        {...register('recurrence_interval_days')}
                      />
                      {errors.recurrence_interval_days && (
                        <p className="text-sm text-destructive">{errors.recurrence_interval_days.message}</p>
                      )}
                    </div>
                  )}

                  {recurrenceRule === 'weekly_days' && (
                    <div className="space-y-2">
                      <Label>Repeat on these days</Label>
                      <Controller
                        control={control}
                        name="recurrence_weekdays"
                        render={({ field }) => (
                          <div className="flex flex-wrap gap-2">
                            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((label, dayIndex) => {
                              const isSelected = field.value.includes(dayIndex)
                              return (
                                <Button
                                  key={label}
                                  type="button"
                                  size="sm"
                                  variant={isSelected ? 'default' : 'outline'}
                                  onClick={() =>
                                    field.onChange(
                                      isSelected
                                        ? field.value.filter((day) => day !== dayIndex)
                                        : [...field.value, dayIndex].sort((a, b) => a - b)
                                    )
                                  }
                                >
                                  {label}
                                </Button>
                              )
                            })}
                          </div>
                        )}
                      />
                      {errors.recurrence_weekdays && (
                        <p className="text-sm text-destructive">{errors.recurrence_weekdays.message}</p>
                      )}
                    </div>
                  )}

                  {recurrenceRule === 'monthly_day' && (
                    <div className="space-y-2">
                      <Label htmlFor="recurrence_month_day">Day of month</Label>
                      <Input
                        id="recurrence_month_day"
                        type="number"
                        min="1"
                        max="31"
                        placeholder="e.g. 15"
                        {...register('recurrence_month_day')}
                      />
                      {errors.recurrence_month_day && (
                        <p className="text-sm text-destructive">{errors.recurrence_month_day.message}</p>
                      )}
                    </div>
                  )}

                  {recurrenceRule !== 'none' && !isLightForm && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="recurrence_end">Repeat until</Label>
                        <Input
                          id="recurrence_end"
                          type="date"
                          {...register('recurrence_end')}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="recurrence_count">Number of occurrences (optional)</Label>
                        <Input
                          id="recurrence_count"
                          type="number"
                          min="1"
                          placeholder="Leave blank for open-ended"
                          {...register('recurrence_count')}
                        />
                        {errors.recurrence_count && (
                          <p className="text-sm text-destructive">{errors.recurrence_count.message}</p>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card> : null}

          {/* Budget */}
          {!isLightForm ? <Card>
            <CardHeader>
              <CardTitle className="text-base">Budget</CardTitle>
              <CardDescription>Set job budget</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="budget">Budget Amount</Label>
                <Input
                  id="budget"
                  type="number"
                  placeholder="0.00"
                  step="0.01"
                  {...register('budget')}
                />
              </div>
            </CardContent>
          </Card> : null}
        </div>

        {!isLightForm ? (
          <JobAcceptanceModeFields {...bindJobAcceptanceModeFormFields({ control })} />
        ) : null}

        {!isLightForm ? <JobMediaGalleryEditor
          pendingFiles={pendingMedia}
          onPendingFilesChange={setPendingMedia}
          title={labels.galleryTitle}
          description={isProjectMode
            ? 'Add images and videos now. They upload automatically after the project is created.'
            : 'Add images and videos now. They upload automatically after the job is created.'}
        /> : null}

        {isEventMode && !isCommitteeCreate ? (
          <EventCoverMediaFields
            thumbnailFile={thumbnailFile}
            bannerFile={bannerFile}
            onThumbnailFileChange={setThumbnailFile}
            onBannerFileChange={setBannerFile}
            disabled={createMutation.isPending}
          />
        ) : null}

        {/* Actions */}
        <div className={cn('flex items-center justify-end gap-4', isLightForm && 'max-w-xl')}>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(withServiceInstanceId(listPath, serviceInstanceId))}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={createMutation.isPending || isCategoriesLoading || categories.length === 0}>
            {createMutation.isPending && <Spinner className="mr-2" />}
            {`Create ${createEntityLabel}`}
          </Button>
        </div>
      </form>
      )}

      {isCommitteeCreate ? (
        <Dialog
          open={categoryDialogOpen}
          onOpenChange={(open) => {
            setCategoryDialogOpen(open)
            if (!open) setCategoryName('')
          }}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add category</DialogTitle>
              <DialogDescription>
                The new category is saved for this branch and selected on the committee.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor="new-category-name">Name</Label>
              <Input
                id="new-category-name"
                value={categoryName}
                placeholder="Finance"
                autoFocus
                onChange={(event) => setCategoryName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    submitNewCategory()
                  }
                }}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setCategoryDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                disabled={!categoryName.trim() || createCategoryMutation.isPending}
                onClick={submitNewCategory}
              >
                {createCategoryMutation.isPending ? <Spinner className="mr-2" /> : null}
                Add category
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  )
}
